import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { CourseEnrolment } from 'src/ReduxStorage/InstitutionAdminSettings/types';
import { INSTITUTION, PORTAL, SEARCH } from 'src/ReduxStorage/resourceNames';
import {
  InstitutionResource,
  InstitutionResourceGetCourses,
  InstitutionResourceGetInstitutionCohortsByCoursePath,
} from 'src/resource/Institution/types';
import {
  PortalResource,
  PortalResourceAddEnrolmentsToUserGroups,
  PortalResourceGetCourseSummaries,
  PortalResourceGetUserGroups,
  PortalResourceRemoveEnrolments,
  PortalResourceUpdateRoleForUsers,
  PortalResourceResendInvitationEmails,
  PortalResourceAssignToCourse,
  PortalResourceRemoveFromCourse,
} from 'src/resource/PortalResource/types';
import { ResourceApi } from 'src/resource/Resource';
import {
  SearchResource,
  SearchResourceSuggestUser,
  SearchResourceSuggestUserReturnResult,
} from 'src/resource/SearchResource/types';
import { axiosInstance } from 'src/web/utilities/axios';
import { AssignedCourseRolesType } from './types';

export const useSuggestUserApi = (
  initialSearchQuery = '',
  institutionPath: string
): [
  null | SearchResourceSuggestUserReturnResult[],
  (searchQuery: string) => void,
  () => void
] => {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [queryResult, setQueryResult] = useState<
    null | SearchResourceSuggestUserReturnResult[]
  >(null);

  const debouncedSuggestUserApiCall = useCallback(
    debounce((searchQuery: string, startIndex: number) => {
      // Do the exact api calll
      const params = {
        types: 'user' as const,
        query: searchQuery,
        institutionPaths: [institutionPath],
        start: startIndex,
        filterPublicInstitutions: true,
      };
      return ResourceApi.lookup<SearchResource, SearchResourceSuggestUser>(
        SEARCH,
        'suggestUser',
        params
      ).then(({ results }) => {
        setQueryResult(results);
      });
    }, 300),
    []
  );

  const loadMore = useCallback((): void => {
    debouncedSuggestUserApiCall(searchQuery, (queryResult?.length ?? 0) + 1);
  }, [searchQuery, queryResult]);

  useEffect(() => {
    // Do the Api call everytime search query change
    if (searchQuery !== '') {
      debouncedSuggestUserApiCall(searchQuery, 0);
    } else {
      setQueryResult(null);
    }
  }, [searchQuery]);

  return [queryResult, setSearchQuery, loadMore];
};

export const useGetCoursesInformation = (
  institutionPath: string,
  coursePaths: string[],
  pageSize = 100
): [CourseEnrolment[], boolean, () => void] => {
  const [pageIndex, setPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [coursesData, setCoursesData] = useState<CourseEnrolment[]>([]);

  useEffect(() => {
    if (pageSize > 0) {
      const startLookupCoursePathsIndex = pageIndex * pageSize;
      setIsLoading(true);
      ResourceApi.read<PortalResource, PortalResourceGetCourseSummaries>(
        PORTAL,
        institutionPath,
        'getCourseSummaries',
        {
          coursePaths: coursePaths.slice(
            startLookupCoursePathsIndex,
            startLookupCoursePathsIndex + pageSize
          ),
        }
      ).then((res) => {
        setCoursesData((prev) => [...prev, ...Object.values(res)]);
        setIsLoading(false);
      });
    }
  }, [pageIndex, pageSize]);

  const loadMore = useCallback(() => setPageIndex((prev) => prev + 1), [
    setPageIndex,
  ]);

  return [coursesData, isLoading, loadMore];
};

export const useGetPortalGroupsApi = (
  institutionPath: string
): [PortalResourceGetUserGroups['return']] => {
  const [data, setData] = useState<string[]>([]);
  useEffect(() => {
    ResourceApi.read<PortalResource, PortalResourceGetUserGroups>(
      PORTAL,
      institutionPath,
      'getUserGroups',
      {}
    ).then((res) => {
      setData(res);
    });
  }, [institutionPath]);
  return [data];
};

export const useAddEnrolmentsToUserGroupsApi = (
  institutionPath: string
): {
  addEnrolmentsToUserGroups: (params: {
    userGroups: string[];
    ids: string[];
  }) => Promise<unknown>;
} => {
  const addEnrolmentsToUserGroups = useCallback(
    ({ userGroups, ids }: { userGroups: string[]; ids: string[] }) => {
      return ResourceApi.read<
        PortalResource,
        PortalResourceAddEnrolmentsToUserGroups
      >(PORTAL, institutionPath, 'addEnrolmentsToUserGroups', {
        userGroups,
        enrolmentIds: ids,
      });
    },
    [institutionPath]
  );

  return { addEnrolmentsToUserGroups };
};

export const useRemoveEnrolmentsApi = (
  institutionPath: string
): {
  removeEnrolments: (params: { ids: string[] }) => Promise<unknown>;
} => {
  const removeEnrolments = useCallback(
    ({ ids }: { ids: string[] }) => {
      return ResourceApi.update<PortalResource, PortalResourceRemoveEnrolments>(
        PORTAL,
        institutionPath,
        'removeEnrolments',
        {
          enrolmentIds: ids,
        }
      );
    },
    [institutionPath]
  );

  return { removeEnrolments };
};

export const useUpdateRoleForUsersApi = (
  institutionPath: string
): {
  updateRoleForUsers: (params: {
    ids: string[];
    role: 'staff' | 'admin';
  }) => Promise<unknown>;
} => {
  const updateRoleForUsers = useCallback(
    ({ ids, role }: { ids: string[]; role: 'staff' | 'admin' }) => {
      return ResourceApi.update<
        PortalResource,
        PortalResourceUpdateRoleForUsers
      >(PORTAL, institutionPath, 'updateRoleForUsers', {
        enrolmentIds: ids,
        role,
      });
    },
    [institutionPath]
  );
  return { updateRoleForUsers };
};

export const useResendInvitationEmailsApi = (
  institutionPath: string
): {
  resendInvitationEmails: (params: { ids: string[] }) => Promise<unknown>;
} => {
  const resendInvitationEmails = useCallback(
    ({ ids }: { ids: string[] }) => {
      return ResourceApi.update<
        PortalResource,
        PortalResourceResendInvitationEmails
      >(PORTAL, institutionPath, 'resendInvitationEmails', {
        invitedUserIds: ids,
      });
    },
    [institutionPath]
  );

  return { resendInvitationEmails };
};

export const useUninvitePendingTeamMembersApi = (
  institutionPath: string
): {
  uninvitePendingTeamMembers: (params: { ids: string[] }) => Promise<unknown>;
} => {
  const uninvitePendingTeamMembers = useCallback(
    ({ ids }: { ids: string[] }) => {
      const formData = new FormData();
      formData.append('email', ids.join(','));
      return axiosInstance.post(
        `/api/institution/uninvite/?institution=${institutionPath}`,
        formData
      );
    },
    [institutionPath]
  );
  return { uninvitePendingTeamMembers };
};

export const useGetCoursesApi = (
  institutionPath: string
): InstitutionResourceGetCourses['return'] => {
  const [coursesData, setCoursesData] = useState<
    InstitutionResourceGetCourses['return']
  >([]);
  useEffect(() => {
    ResourceApi.read<InstitutionResource, InstitutionResourceGetCourses>(
      INSTITUTION,
      institutionPath,
      'getCourses',
      {}
    ).then((res) =>
      setCoursesData(
        res.map((course) => ({ ...course, path: course.path.toLowerCase() }))
      )
    );
  }, [institutionPath]);
  return coursesData;
};

export const useGetInstitutionCohortsByCoursePathApi = (
  institutionPath: string,
  coursePath: string
): InstitutionResourceGetInstitutionCohortsByCoursePath['return'] | null => {
  const [cohortsData, setCohortsData] = useState<
    InstitutionResourceGetInstitutionCohortsByCoursePath['return'] | null
  >(null);
  useEffect(() => {
    ResourceApi.read<
      InstitutionResource,
      InstitutionResourceGetInstitutionCohortsByCoursePath
    >(INSTITUTION, institutionPath, 'getInstitutionCohortsByCoursePath', {
      coursePathList: [coursePath],
    }).then(setCohortsData);
  }, [institutionPath, coursePath]);
  return cohortsData;
};

export const useAssignToCourseApi = (
  institutionPath: string
): {
  assignToCourse: (params: {
    ids: string[];
    courseRoles: AssignedCourseRolesType;
  }) => Promise<unknown[]>;
} => {
  /**
   * assignToCourse explanation:
   *
   * Sample input:
   *  - ids: ['user1','user2','user3']
   *  - courseRoles: {
   *      'coursePath1': {
   *        role: 'admin',
   *       },
   *      'coursePath2`: {
   *        role: 'staff',
   *        cohortRoles: {
   *          'cohortPath1': {
   *            facilitator: false,
   *            assessor: true,
   *            designer: true,
   *          },
   *          'cohortPath2': {
   *            facilitator: false,
   *            assessor: false,
   *            designer: false,
   *          }
   *        }
   *      },
   *      'coursePath3': {
   *        role: 'staff',
   *        cohortRoles: {
   *          'cohortPath3': {
   *            facilitator: true,
   *            assessor: true,
   *            designer: true,
   *          }
   *        }
   *      }
   *    }
   *
   * Should return:
   * - Promise.all([
   *    assignToCourse({ role: 'admin', coursePath: 'coursePath1', userIds: ['user1','user2','user3'] }),
   *    assignToCourse({ role: 'staff', cohortPath: 'cohortPath1', userIds: ['user1','user2','user3'], roles: ['assessor','assessment_designer'] }),
   *    assignToCourse({ role: 'staff', cohortPath: 'cohortPath2', userIds: ['user1','user2','user3'], roles: [] }),
   *    assignToCourse({ role: 'staff', cohortPath: 'cohortPath3', userIds: ['user1','user2','user3'], roles: ['facilitator','assessor','assessment_designer'] }),
   *  ])
   */
  // @TODO: create a test case for this assignToCourses
  const assignToCourse = useCallback(
    ({
      ids,
      courseRoles,
    }: {
      ids: string[];
      courseRoles: AssignedCourseRolesType;
    }) => {
      const promises: Promise<unknown>[] = [];

      Object.entries(courseRoles).forEach(([coursePath, courseRole]) => {
        if (courseRole.role === 'admin') {
          promises.push(
            ResourceApi.update<PortalResource, PortalResourceAssignToCourse>(
              PORTAL,
              institutionPath,
              'assignToCourse',
              { role: 'admin', coursePath, userIds: ids }
            )
          );
        } else if (courseRole.role === 'staff') {
          const { cohortRoles } = courseRole;
          Object.entries(cohortRoles).forEach(([cohortPath, cohortRole]) => {
            const roles = Object.keys(cohortRole)
              .map((roleType) =>
                cohortRole[roleType as keyof typeof cohortRole]
                  ? roleType.replace('designer', 'assessment_designer')
                  : ''
              )
              .filter((role) => role);
            promises.push(
              ResourceApi.update<PortalResource, PortalResourceAssignToCourse>(
                PORTAL,
                institutionPath,
                'assignToCourse',
                {
                  role: 'staff',
                  cohortPath,
                  userIds: ids,
                  roles: roles as (
                    | 'facilitator'
                    | 'assessor'
                    | 'assessment_designer'
                  )[],
                }
              )
            );
          });
        }
      });
      return Promise.all(promises);
    },
    [institutionPath]
  );

  return { assignToCourse };
};

export const useRemoveFromCourseApi = (
  institutionPath: string
): {
  removeFromCourse: (param: {
    ids: string[];
    coursePaths: string[];
  }) => Promise<unknown[]>;
} => {
  const removeFromCourse = useCallback(
    ({ ids, coursePaths }: { ids: string[]; coursePaths: string[] }) => {
      return Promise.all(
        coursePaths.map((coursePath) =>
          ResourceApi.update<PortalResource, PortalResourceRemoveFromCourse>(
            PORTAL,
            institutionPath,
            'removeFromCourse',
            { userIds: ids, coursePath }
          )
        )
      );
    },
    [institutionPath]
  );
  return { removeFromCourse };
};
